import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

function SolutionsHub() {
  useSEO({
    title: 'Solutions',
    description: 'Simulation-driven solutions for operations, workforce, supply chain, healthcare, energy, and more.'
  });

  const solutions = [
    {
      category: 'Operations',
      title: 'Asset + Fleet Management',
      excerpt: 'Balance utilization and availability across vehicle fleets, aircraft, heavy equipment, and other physical assets. Model maintenance cycles, operational demand, and resource constraints.',
      href: '/solutions/asset-fleet',
      industries: ['Aviation', 'Trucking', 'Mining', 'Equipment Rental']
    },
    {
      category: 'Operations',
      title: 'Inventory + Production',
      excerpt: 'Optimize inventory levels, production schedules, and supply chain dynamics. Simulate demand variability, lead times, and capacity constraints to reduce stockouts and excess.',
      href: '/solutions/supply-chain',
      industries: ['Manufacturing', 'Retail', 'Distribution', 'CPG']
    },
    {
      category: 'Commercial',
      title: 'Demand + Pricing',
      excerpt: 'Model price elasticity, promotional impact, and competitive dynamics. Test pricing strategies and demand scenarios before committing to market.',
      href: '/solutions/retail-ecommerce',
      industries: ['Retail', 'E-commerce', 'Hospitality', 'SaaS']
    },
    {
      category: 'Commercial',
      title: 'Pipeline + Revenue',
      excerpt: 'Forecast revenue with scenario-based pipeline modeling. Understand how conversion rates, deal cycles, and market conditions affect your numbers.',
      href: '/solutions/revenue-pipeline',
      industries: ['B2B Sales', 'SaaS', 'Professional Services', 'Financial Services']
    },
    {
      category: 'Workforce',
      title: 'Capacity + Backlog',
      excerpt: 'Balance workload against available capacity. Model hiring decisions, attrition, training ramp-up, and backlog dynamics to maintain throughput.',
      href: '/solutions/workforce',
      industries: ['Professional Services', 'Technology', 'Call Centers', 'Healthcare']
    },
    {
      category: 'Workforce',
      title: 'Scheduling + Throughput',
      excerpt: 'Optimize scheduling for constrained resources. Model patient flow, operating room utilization, staff scheduling, and bottleneck dynamics.',
      href: '/solutions/hospital-or',
      industries: ['Healthcare', 'Manufacturing', 'Logistics', 'Service Operations']
    },
    {
      category: 'Infrastructure',
      title: 'Grid + Demand',
      excerpt: 'Model energy systems, grid capacity, and demand patterns. Simulate renewable integration, storage economics, and load management strategies.',
      href: '/solutions/energy-utilities',
      industries: ['Utilities', 'Renewable Energy', 'Grid Operators', 'Industrial']
    },
    {
      category: 'Public Sector',
      title: 'Policy + Impact',
      excerpt: 'Analyze policy interventions before implementation. Model population dynamics, resource allocation, and long-term consequences of policy choices.',
      href: '/solutions/public-policy',
      industries: ['Government', 'NGOs', 'Think Tanks', 'Healthcare Systems']
    },
  ];

  const categories = [...new Set(solutions.map(s => s.category))];

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Hero Section - Executive-grade, outcome-focused */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-20 md:py-32 text-center">
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Decision Intelligence Platform</p>
        <h1
          className="mb-6 tracking-tight w-screen max-w-none relative left-1/2 -translate-x-1/2 px-4 sm:static sm:translate-x-0 sm:w-full sm:max-w-full sm:px-0"
          style={{
            fontSize: 'clamp(2.5rem, 10vw, 4.4rem)',
            fontWeight: 320,
            margin: 0,
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #3d1549 0%, #643a71 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em',
            lineHeight: 1.02,
            paddingBottom: '0.15em',
          }}
        >
          The Flight Simulator <span className="block sm:inline whitespace-nowrap">for Enterprise Strategy.</span>
        </h1>

        <p className="text-2xl md:text-3xl text-gray-800 mb-8 max-w-3xl mx-auto leading-tight">
          Crash the model, <span className="whitespace-nowrap">not the business.</span>
        </p>

        <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
          Turn your enterprise into a live simulation. Stress-test your next big bet in real-time, visualize the impact, and move from 'what if' to <span className="whitespace-nowrap">'what’s next'</span> with total confidence.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <a href="/contact">
            <button className="bg-gray-900 text-white px-8 py-4 rounded text-base font-medium hover:bg-gray-800 transition-colors min-w-[220px]">
              Schedule a conversation
            </button>
          </a>
          <a href="/request-access" target="_blank" rel="noopener noreferrer">
            <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded text-base font-medium hover:border-gray-400 hover:bg-gray-50 transition-colors min-w-[220px]">
              Request Immediate Access
            </button>
          </a>
        </div>

        {/* Abstract systems visual placeholder */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-0">
            <img 
              src="/preview-1.png" 
              alt="Simulation model showing system dynamics and scenario outcomes" 
              className="w-full h-auto rounded border border-black"
            />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Decision Support powered by <span className="whitespace-nowrap">AI-Native Digital Twins</span>
          </p>
          
        </div>
      </main>

      <main className="max-w-4xl mx-auto px-8">
        {/* System Navigation Section */}
        <section className="relative left-1/2 right-1/2 w-screen -ml-[50vw] -mr-[50vw] bg-gray-50 py-14 mb-16">
          <div className="max-w-4xl mx-auto px-8">
            <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4 tracking-tight">
              Navigate the System, Not just the Silos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Most organizations manage their business in fragments. Veydra creates an Integrated Digital Twin that reveals how a change in one department creates a ripple effect across the entire enterprise.
            </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-left">
                <h3 className="text-xl font-medium text-gray-900 mb-3">Reveal Hidden Dependencies</h3>
                <p className="text-gray-600 leading-relaxed">
                  See how increasing production speed (Ops) might strain your maintenance budget or decrease asset longevity (Finance).
                </p>
              </div>

              <div className="text-left">
                <h3 className="text-xl font-medium text-gray-900 mb-3">Simulate Second-Order Effects</h3>
                <p className="text-gray-600 leading-relaxed">
                  Understand how a gain in efficiency in one function creates a bottleneck in another downstream.
                </p>
              </div>

              <div className="text-left">
                <h3 className="text-xl font-medium text-gray-900 mb-3">Dynamic ROI Scaling</h3>
                <p className="text-gray-600 leading-relaxed">
                  Map micro-level operational data to macro-level business outcomes in real-time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions by Category */}
        {categories.map((category, catIndex) => (
          <div key={category} className="mb-16">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-8">{category}</h2>
            <div className="space-y-10">
              {solutions
                .filter(s => s.category === category)
                .map((solution, index, filtered) => (
                  <article key={solution.href} className="group">
                    <Link to={solution.href} className="block">
                      <div className="flex items-start gap-6">
                        {/* Icon */}
                        <div className="hidden md:flex w-16 h-16 bg-gray-100 border border-gray-200 rounded-lg flex-shrink-0 items-center justify-center text-gray-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-medium text-gray-900 group-hover:text-gray-600 mb-2 transition-colors">
                            {solution.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed mb-3">
                            {solution.excerpt}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {solution.industries.map(industry => (
                              <span key={industry} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {industry}
                              </span>
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-600">
                            See examples →
                          </span>
                        </div>
                      </div>
                    </Link>
                    
                    {index < filtered.length - 1 && (
                      <div className="border-b border-gray-100 mt-10" />
                    )}
                  </article>
                ))}
            </div>
            {catIndex < categories.length - 1 && (
              <div className="border-b border-gray-200 mt-12" />
            )}
          </div>
        ))}

        {/* CTA */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <h2 className="text-xl font-medium text-gray-900 mb-3">Don't see your use case?</h2>
          <p className="text-gray-600 mb-6">Our platform is flexible enough to model most complex systems. Let's discuss your challenge.</p>
          <a href="/contact">
            <button className="border border-gray-900 text-gray-900 px-6 py-3 rounded text-sm font-medium hover:bg-gray-900 hover:text-white transition-colors">
              Schedule a conversation →
            </button>
          </a>
        </div>
      </main>


      {/* Two Paths Section - SaaS vs Consulting */}
      <section className="py-20 md:py-28 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4 tracking-tight">
              Two ways to work with us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you want hands-on control or expert guidance, we meet you where you are.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* SaaS Path - Light */}
            <div className="bg-gray-100 rounded-lg p-8 md:p-10 hover:bg-gray-200 transition-colors flex flex-col">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Start Now</div>
              <h3 className="text-4xl font-large text-gray-900 mb-4">Self-serve platform</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Build and run your own simulation models with a rigorous, configurable platform. 
                Ideal for analysts, strategists, and teams who want direct control.
              </p>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start text-gray-600">
                  <span className="text-gray-400 mr-3">—</span>
                  Full modeling environment with system dynamics
                </li>
                <li className="flex items-start text-gray-600">
                  <span className="text-gray-400 mr-3">—</span>
                  AI-assisted model building and calibration
                </li>
                <li className="flex items-start text-gray-600">
                  <span className="text-gray-400 mr-3">—</span>
                  Scenario comparison and sensitivity analysis
                </li>
                <li className="flex items-start text-gray-600">
                  <span className="text-gray-400 mr-3">—</span>
                  Shareable dashboards and reports
                </li>
              </ul>
              <a href="/request-access" target="_blank" rel="noopener noreferrer" className="mt-auto">
                <button className="w-full border border-gray-900 text-gray-900 px-6 py-3 rounded text-sm font-medium hover:bg-gray-900 hover:text-white transition-colors">
                  Get Immediate Access Now →
                </button>
              </a>
            </div>

            {/* Consulting Path */}
            <div className="bg-gray-900 rounded-lg p-8 md:p-10 hover:bg-gray-800 transition-colors flex flex-col">
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Consulting</div>
              <h3 className="text-4xl font-large text-white mb-4">Expert-led engagement</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Partner with our team for high-stakes decisions that require bespoke modeling, 
                deep analysis, and executive-ready insights.
              </p>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start text-gray-300">
                  <span className="text-gray-500 mr-3">—</span>
                  Custom model development for your context
                </li>
                <li className="flex items-start text-gray-300">
                  <span className="text-gray-500 mr-3">—</span>
                  Facilitated scenario workshops with leadership
                </li>
                <li className="flex items-start text-gray-300">
                  <span className="text-gray-500 mr-3">—</span>
                  Ongoing decision support and model refinement
                </li>
                <li className="flex items-start text-gray-300">
                  <span className="text-gray-500 mr-3">—</span>
                  Board-ready presentations and recommendations
                </li>
              </ul>
              <a href="/contact" className="mt-auto">
                <button className="w-full border border-white text-white px-6 py-3 rounded text-sm font-medium hover:bg-white hover:text-gray-900 transition-colors">
                  Schedule a conversation →
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default SolutionsHub;
